import { hasRevocableAccess } from "../../../common/schlageAccessState";
import useSchlageAccessRefresh from "./useSchlageAccessRefresh";
import useKeysReportRefresh from "./useKeysReportRefresh";
import {
  getOfferCapabilities,
  supportsOfflineFallback,
} from "../../../common/offerCapabilities";
import React, {
  useEffect,
  useState,
  useContext,
  useMemo,
  useCallback,
  useRef,
} from "react";
import { useParams } from "react-router-dom";
import { CopyToClipboard } from "react-copy-to-clipboard";
import {
  ResponsiveContext,
  Grid,
  Header,
  Box,
  Heading,
  Button,
  FormField,
  TextInput,
  Text,
  CheckBox,
  Image,
  Tabs,
  Tab,
  Tip,
  Anchor,
} from "grommet";
import { FormNextLink, StatusWarning } from "grommet-icons";
import moment from "moment-timezone";
import { formatPhoneNumberIntl } from "react-phone-number-input";
import { useTranslation } from "react-i18next";
import {
  GuestService,
  CheckinService,
  ChargeService,
  NotificationService,
  AuthService,
} from "@services";
import {
  BookingCalendarInput,
  BookingStatus,
  SecuredImage,
  HourInput,
  SmsUnreachableIcon,
} from "@shared";
import { RoomSelect } from "@dashboard";
import {
  VerifyCheckInModal,
  Conversation,
  EventList,
  ViewMenuRequestModal,
  KeysSentReportModal,
  AccessList,
  RemoteUnlockButton,
  CheckKeyUsedButton,
} from "./components";
import { presentToastSuccess, presentToastErrorContent } from "@shared/Toast";
import { presentLoading, dismissLoading } from "@shared/Loading";
import { useFetchDoors, useFetchHotel } from "@dashboard/redux/hooks";
import { presentAlertPrompt } from "@shared/Alert";
import placeholderGuest from "@images/placeholder-guest.png";

import "./GuestDetails.scss";
import { ButtonWithConfirm } from "@features/shared";
import { formatMoney, getMenuSubtotal } from "@common/utils";

const defaultValues = {
  _id: 0,
  firstname: "",
  lastname: "",
  email: "",
  phone: "",
  checkinDate: moment().hour(15),
  checkoutDate: moment().add(1, "days").hour(12),
  checkinTime: "15:00",
  checkoutTime: "12:00",
  roomNumber: "",
  roomType: 0,
  passcodes: [],
  currentDoors: [],
  invitationSent: false,
  status: {},
  onlineCheckin: {},
  magicLink: "",
  linkUsed: false,
  charges: [],
  messages: [],
  passcodeSent: null,
  shouldTriggerSendKeys: false,
  forceRandomPasscode: false,
  validNow: false,
};

const rows = {
  small: ["auto", "auto", "auto", "auto", "auto"],
  medium: ["auto", "auto", "auto"],
};

const tabMap = {
  info: 0,
  messages: 1,
  events: 2,
};

export default function GuestDetails(props) {
  var contextSize = useContext(ResponsiveContext);
  if (contextSize !== "small") contextSize = "medium";

  var { guestId: bookingDBId, tab } = useParams();
  const { hotel, fetchHotel } = useFetchHotel();
  const { doors, rooms } = useFetchDoors();
  const { t } = useTranslation();
  const [formValues, setFormValues] = useState(defaultValues);
  const [accessDoors, setAccessDoors] = useState([]);
  const [booking, setBooking] = useState({});
  const [keysSentReport, setKeysSentReport] = useState(null);
  const keyRequest = useRef(0);
  useEffect(() => {
    keyRequest.current += 1;
    setKeysSentReport(null);
  }, [bookingDBId, hotel?._id]);
  const refreshKeysReport = useKeysReportRefresh({
    bookingId: bookingDBId,
    hotel,
    report: keysSentReport,
    setReport: setKeysSentReport,
    setFormValues,
  });
  const { refreshError: accessRefreshError } = useSchlageAccessRefresh({
    bookingId: bookingDBId,
    hotel,
    passcodes: formValues.passcodes,
    setFormValues,
  });
  const refreshAccess = useCallback(async () => {
    try {
      const { data } = await GuestService.getBooking(bookingDBId);
      setFormValues((previous) =>
        String(previous._id) === String(bookingDBId)
          ? {
              ...previous,
              passcodes: data.passcodes || [],
              ekeys: data.ekeys || [],
            }
          : previous,
      );
    } catch (error) {
      presentToastErrorContent(error);
    }
  }, [bookingDBId]);

  const [tabIndex, setTabIndex] = useState(tabMap[tab] || 0);
  const onActive = (nextIndex) => setTabIndex(nextIndex);
  const caps = getOfferCapabilities(hotel);
  const isOracode = caps.accessDoors && hotel?.lockType === "oracode";
  const canRemoteUnlock =
    caps.accessDoors &&
    (hotel?.lockType === "schlage"
      ? hotel?.capabilities?.remoteUnlock === true
      : (!hotel?.lockType || hotel.lockType === "ttlock") &&
        hotel?.hasWifiGateway);
  const currentRoomType = rooms?.find(
    (r) => r.name === formValues.roomNumber,
  )?.roomType;
  const showEarlyCheckinOffer = Boolean(
    caps.upsells &&
    hotel?.offerEarlyCheckin &&
    !formValues.charges.some((charge) => charge.reference === "earlyCheckin") &&
    currentRoomType?.earlyCheckinPrice > 0,
  );
  const showUpgradeOffer = Boolean(
    caps.upsells &&
    hotel?.offerUpgrade &&
    !formValues.charges.some((charge) => charge.reference === "upgrade") &&
    currentRoomType?.upgradePrice > 0,
  );
  const showLateCheckoutOffer = Boolean(
    caps.upsells &&
    hotel?.offerLateCheckout &&
    !formValues.charges.some((charge) => charge.reference === "lateCheckout") &&
    currentRoomType?.lateCheckoutPrice > 0,
  );

  const gridAreas = useMemo(
    () =>
      !caps.accessDoors
        ? {
            small: [
              ["info", "info"],
              ["status", "status"],
              ["upsell", "upsell"],
              ["magicLink", "magicLink"],
            ],
            medium: [
              ["info", "status"],
              ["upsell", "upsell"],
              ["magicLink", "magicLink"],
            ],
          }
        : {
            small: [
              ["info", "info"],
              ["status", "status"],
              ["upsell", "upsell"],
              ["keys", "keys"],
              ...(hotel?.usePasscodes ? [["passcode", "passcode"]] : []),
              ["ekeys", "ekeys"],
              ["magicLink", "magicLink"],
            ],
            medium: [
              ["info", "status"],
              ["upsell", "keys"],
              ...(hotel?.usePasscodes ? [["passcode", "passcode"]] : []),
              ["ekeys", "ekeys"],
              ["magicLink", "magicLink"],
            ],
          },
    [hotel?.usePasscodes, caps.accessDoors],
  );

  useEffect(() => {
    if (!booking?.hotel || !hotel?._id) return;
    const bookingHotelId = String(booking.hotel?._id || booking.hotel);
    if (bookingHotelId === String(hotel._id)) return;
    // Booking belongs to a different hotel in the same group — switch to it.
    AuthService.selectProperty(bookingHotelId)
      .then(() => {
        const hotelName =
          booking.hotel?.name || "the property this reservation belongs to";
        presentAlertPrompt({
          title: "Property switched",
          message: `You have been switched to "${hotelName}".`,
          buttonOKText: "OK",
          onOK: () => window.location.reload(),
          onCancel: () => window.location.reload(),
        });
      })
      .catch((err) => {
        console.error("Failed to switch hotel:", err);
      });
  }, [booking, hotel._id]);

  useEffect(() => {
    if (doors?.length && booking?.roomNumber) {
      const allDoors = [
        ...doors.filter((x) => x.lockId && !x.disabled && x.type === "area"),
        ...doors
          .filter(
            (x) =>
              (x.lockId || x.validNoLock) && !x.disabled && x.type === "room",
          )
          .sort((a, b) => (a.name < b.name ? -1 : 1)),
      ];

      const suggestedRoomObj = allDoors.find(
        (x) => x.name === booking.roomNumber,
      );
      const suggestedSelected = allDoors.filter(
        (door) =>
          door.name === booking.roomNumber ||
          (door.type === "area" &&
            ((door.restrictedToDoorIds || []).length === 0 ||
              door.restrictedToDoorIds.includes(suggestedRoomObj?._id))),
      );
      setAccessDoors(suggestedSelected);
    }
  }, [doors, booking.roomNumber]);

  const refreshDetails = useCallback(
    async (id = bookingDBId) => {
      presentLoading();
      await GuestService.getBooking(id).then(
        (response) => {
          dismissLoading();
          const booking = response.data;
          setBooking(booking);
        },
        (error) => {
          dismissLoading();
          presentToastErrorContent(error);
        },
      );

      //setTimeout(() => PmsService.updateDoorStatus(), 1000);
    },
    [bookingDBId],
  );

  useEffect(() => {
    if (booking?._id) {
      setFormValues({
        ...booking,
        currentDoors: [],
        passcodes: booking.passcodes || [],
        checkinDate: moment.tz(booking.checkinDate, hotel.timezone),
        checkoutDate: moment.tz(booking.checkoutDate, hotel.timezone),
        checkinTime: moment
          .tz(booking.checkinDate, hotel.timezone)
          .format("HH:mm"),
        checkoutTime: moment
          .tz(booking.checkoutDate, hotel.timezone)
          .format("HH:mm"),
        guestId: booking.guest._id,
        shouldTriggerSendKeys: false,
      });
    }
  }, [booking, hotel.timezone]);

  useEffect(() => {
    if (tabIndex === 1) {
      NotificationService.removeMessage(bookingDBId).then((response) => {
        fetchHotel();
      });
    }
  }, [bookingDBId, fetchHotel, tabIndex]);

  useEffect(() => {
    if (bookingDBId !== 0) {
      refreshDetails(bookingDBId);
    }
  }, [bookingDBId, hotel.timezone, refreshDetails]);

  function handleInputChange({ target: { name, value } }) {
    if (disabled()) return;
    var values = { ...formValues };
    values[name] = value;
    setFormValues(values);
  }

  function handleRoomInputChange({ name, roomType }) {
    var values = { ...formValues, roomNumber: name };
    if (roomType) values.roomType = roomType;
    setFormValues({ ...values, shouldTriggerSendKeys: true });
  }

  function handleCheckboxChange({ target: { name, checked } }) {
    if (disabled()) return;
    var values = { ...formValues };
    values[name] = checked;
    setFormValues(values);
  }

  function handleBookingDatesChange(checkinDate, checkoutDate) {
    var checkinDateString = moment(checkinDate).format("YYYY-MM-DD");
    var checkoutDateString = moment(checkoutDate).format("YYYY-MM-DD");
    checkinDate = moment
      .tz(checkinDateString + " " + formValues.checkinTime, hotel.timezone)
      .utc();
    checkoutDate = moment
      .tz(checkoutDateString + " " + formValues.checkoutTime, hotel.timezone)
      .utc();
    var values = { ...formValues, checkinDate, checkoutDate };
    setFormValues(values);

    setFormValues({ ...values, shouldTriggerSendKeys: true });
  }

  function handleHourInputChange({ name, value }) {
    var values = { ...formValues };
    values[name] = value;
    if (name === "checkinTime") {
      var checkinDateStr = moment
        .tz(formValues.checkinDate, hotel.timezone)
        .format("YYYY-MM-DD");
      values.checkinDate = moment
        .tz(checkinDateStr + " " + value, hotel.timezone)
        .utc();
    } else if (name === "checkoutTime") {
      var checkoutDateStr = moment
        .tz(formValues.checkoutDate, hotel.timezone)
        .format("YYYY-MM-DD");
      values.checkoutDate = moment
        .tz(checkoutDateStr + " " + value, hotel.timezone)
        .utc();
    }

    setFormValues({ ...values, shouldTriggerSendKeys: true });
  }

  const handleUpdate = async (action, restartCheckin) => {
    if (formValues._id !== 0) {
      var request = { ...formValues };
      if (action === "info") {
        const {
          _id,
          firstname,
          lastname,
          email,
          phone,
          checkinDate,
          checkoutDate,
          roomType,
          guestId,
          roomNumber,
        } = formValues;
        request = {
          _id,
          firstname,
          lastname,
          email,
          phone,
          checkinDate,
          checkoutDate,
          roomType,
          guestId,
          roomNumber,
        };
        if (
          action === "info" &&
          (formValues.currentDoors?.length > 0 ||
            formValues.passcodes.length > 0) &&
          caps.accessDoors &&
          formValues.shouldTriggerSendKeys
        ) {
          presentAlertPrompt({
            title: "Updating keys",
            message:
              "This will update the keys and passcodes with the new check-in and check-out times. We recommend that you update this information from your PMS, if it's possible. Do you still want to proceed?",
            onOK: () => doUpdateBooking(request, true),
            buttonOKText: "Yes, update",
            buttonOKColor: "brand",
          });
        } else {
          await doUpdateBooking(request, false);
        }
      } else if (action === "upsell") {
        const { _id, offerEarlyCheckin, offerUpgrade, offerLateCheckout } =
          formValues;
        request = {
          _id,
          ...(caps.upsells
            ? { offerEarlyCheckin, offerUpgrade, offerLateCheckout }
            : {}),
          restartCheckin,
        };
        await doUpdateBooking(request);
      }
    }
  };

  const doUpdateBooking = async (request, sendKeys) => {
    try {
      const response = await GuestService.updateBooking(request);
      if (response.data.ok) {
        if (typeof formValues.offerEarlyCheckin !== "boolean") {
          setFormValues({
            ...formValues,
            checkinDate: formValues.checkinDate,
            checkoutDate: formValues.checkoutDate,
            offerEarlyCheckin: true,
            offerUpgrade: true,
            offerLateCheckout: true,
          });
        }

        if (response.data.modifiedCount || response.data.guestModified) {
          presentToastSuccess("Guest details updated");
          if (caps.accessDoors && sendKeys) {
            handleSendKeys("update");
          } else {
            refreshDetails(bookingDBId);
          }
        } else {
          presentToastSuccess("No changes detected");
        }
      }
    } catch (error) {
      presentToastErrorContent(error);
    }
  };

  const handleUpdateForceRandomPasscode = async ({
    target: { name, checked },
  }) => {
    setFormValues({ ...formValues, forceRandomPasscode: checked });
    const response = await GuestService.updateBooking({
      forceRandomPasscode: checked,
      _id: formValues._id,
    });
    if (!response.data.ok) {
      setFormValues({ ...formValues, forceRandomPasscode: !checked });
    }
  };

  const handleValidNowChange = ({ target: { checked } }) => {
    setFormValues({ ...formValues, validNow: checked });
  };

  const handleSendKeys = (from) => {
    const requestId = ++keyRequest.current;
    setKeysSentReport({ loading: true, requestId });
    GuestService.sendKeys(
      formValues._id,
      formValues.roomNumber,
      accessDoors,
      formValues.checkinDate,
      formValues.checkoutDate,
      from,
      formValues.validNow,
    ).then(
      (response) => {
        if (keyRequest.current !== requestId) return;
        setKeysSentReport(previous => previous?.requestId === requestId ? {
          ...response.data,
          requestId,
          pendingChannels: response.data.pendingChannels || {
            text: !!formValues.phone, email: !!formValues.email, push: false,
          },
        } : previous);
        if (!response.data?.pending) presentToastSuccess("Door access updated");
        refreshDetails(bookingDBId);
      },
      (error) => {
        if (keyRequest.current !== requestId) return;
        dismissLoading();
        presentToastErrorContent(error);
        const details =
          error.response?.data?.report || error.response?.data || {};
        setKeysSentReport(previous => previous?.requestId === requestId ? {
          ...details,
          requestId,
          pendingChannels: details.pendingChannels || {
            text: !!formValues.phone, email: !!formValues.email, push: false,
          },
          error: details.message || error.message,
        } : previous);
        if (hotel?.lockType === "schlage") refreshAccess();
      },
    );
  };

  const deleteAllKeys = () => {
    const requestId = ++keyRequest.current;
    setKeysSentReport({ loading: true, removalOnly: true, requestId });
    GuestService.sendKeys(
      formValues._id,
      formValues.roomNumber,
      [],
      formValues.checkinDate,
      formValues.checkoutDate,
    ).then(
      (response) => {
        if (keyRequest.current !== requestId) return;
        setKeysSentReport(previous => previous?.requestId === requestId
          ? { ...response.data, removalOnly: true, requestId } : previous);
        if (!response.data?.pending) presentToastSuccess("All doors access removed");
        refreshDetails(bookingDBId);
      },
      (error) => {
        if (keyRequest.current !== requestId) return;
        dismissLoading();
        presentToastErrorContent(error);
        const details =
          error.response?.data?.report || error.response?.data || {};
        setKeysSentReport(previous => previous?.requestId === requestId ? {
          ...details,
          removalOnly: true,
          requestId,
          error: details.message || error.message,
        } : previous);
        if (hotel?.lockType === "schlage") refreshAccess();
      },
    );
  };

  const closeKeysSentReport = () => {
    setKeysSentReport(null);
  };

  const handleCheckout = () => {
    presentAlertPrompt({
      title: "Are you sure?",
      message:
        "This will update the check-out date and revoke the mobile key. The guest will be warned by text/email/notification.",
      onOK: checkoutGuest,
      buttonOKText: "Check out",
      buttonOKColor: "status-critical",
    });
  };

  const checkoutGuest = () => {
    presentLoading();
    GuestService.checkout(bookingDBId).then(
      (response) => {
        dismissLoading();
        presentToastSuccess("The guest has successfully checked out");
        refreshDetails(bookingDBId);
      },
      (error) => {
        dismissLoading();
        presentToastErrorContent(error);
      },
    );
  };

  const handleSubmitVerification = (approved) => {
    presentLoading();
    CheckinService.approve(formValues._id, approved).then(
      (response) => {
        dismissLoading();
        presentToastSuccess("Status updated");
        refreshDetails(bookingDBId);
      },
      (error) => {
        dismissLoading();
        presentToastErrorContent(error);
      },
    );
  };

  const disabled = () => {
    return (
      formValues.status.slug === "checkedout" ||
      formValues.status.slug === "noshow" ||
      formValues.status.slug === "cancelled"
    );
  };

  const handleBack = () => {
    var index = window.location.href.lastIndexOf("/");
    window.location.href = window.location.href.substring(0, index);
  };

  const renderCharge = (charge) => {
    const isIntegratedProduct = Boolean(charge?.product?.commerceConnection);
    const integrationName = charge?.product?.commerceProvider
      ? charge.product.commerceProvider.charAt(0).toUpperCase() +
        charge.product.commerceProvider.slice(1)
      : "integration";
    var label = "";
    var color = "";
    switch (
      isIntegratedProduct ? charge.commerce?.providerStatus : charge.status
    ) {
      case "posted":
        color = "status-ok";
        label = `Sent to ${integrationName}`;
        break;
      case "posting":
        color = "status-warning";
        label = `Sending to ${integrationName}`;
        break;
      case "failed":
        color = "status-error";
        label = `Could not send to ${integrationName}`;
        break;
      case "pending":
        color = "status-warning";
        label = "Requested";
        break;
      case "confirmed":
        color = "status-ok";
        label = "Confirmed";
        break;
      case "cancelled":
        color = "status-error";
        label = "Cancelled";
        break;
      case "requested":
        color = "status-warning";
        label = "Requested";
        break;
      case "delivered":
        color = "neutral-3";
        label = "Delivered";
        break;
      default:
        label = "";
    }
    const statusBadge = (
      <Box
        pad={{ horizontal: "small", vertical: "2px" }}
        round="small"
        border={{ color: color, size: "small" }}
      >
        <Text size="12px" color={color}>
          {label}
        </Text>
      </Box>
    );
    return (
      <Box
        key={charge._id}
        border={{ color: "light-4", size: "small" }}
        round="small"
        pad="small"
        gap="xsmall"
      >
        <Box justify="between" align="center" direction="row">
          <Text weight="bold">{charge.name}</Text>
          <Box direction="row" align="center" gap="small">
            {charge.commerce?.providerStatus === "failed" &&
            charge.commerce?.error ? (
              <Tip content={charge.commerce.error}>{statusBadge}</Tip>
            ) : (
              statusBadge
            )}
            {charge.status === "requested" &&
              !isIntegratedProduct &&
              (charge.reference === "menu" ? (
                <ViewMenuRequestModal
                  onDecline={() => {
                    presentLoading();
                    ChargeService.cancel(charge._id).then(
                      () => {
                        dismissLoading();
                        presentToastSuccess(
                          "Service cancelled. A email has been sent to the guest.",
                        );
                        refreshDetails(bookingDBId);
                      },
                      (error) => {
                        dismissLoading();
                        presentToastErrorContent(error);
                      },
                    );
                  }}
                  onApprove={() => {
                    presentLoading();
                    ChargeService.approve(charge._id).then(
                      () => {
                        dismissLoading();
                        presentToastSuccess(
                          "Service approved. A email has been sent to the guest.",
                        );
                        refreshDetails(bookingDBId);
                      },
                      (error) => {
                        dismissLoading();
                        presentToastErrorContent(error);
                      },
                    );
                  }}
                  hotel={hotel}
                  chargeId={charge._id}
                  status={charge.status}
                />
              ) : (
                <Box direction="row" gap="small">
                  <ButtonWithConfirm
                    label={t("common.decline")}
                    size="small"
                    primary
                    color="status-error"
                    alertTitle="Decline service"
                    alertMessage={`Are you sure you want to decline "${charge.name}"? The guest will be notified.`}
                    onOK={() => {
                      presentLoading();
                      ChargeService.cancel(charge._id).then(
                        () => {
                          dismissLoading();
                          presentToastSuccess(
                            "Service cancelled. A email has been sent to the guest.",
                          );
                          refreshDetails(bookingDBId);
                        },
                        (error) => {
                          dismissLoading();
                          presentToastErrorContent(error);
                        },
                      );
                    }}
                    buttonOKText={t("common.decline")}
                  />
                  <ButtonWithConfirm
                    label={t("common.approve")}
                    size="small"
                    primary
                    color="status-ok"
                    alertTitle="Approve service"
                    alertMessage={`Are you sure you want to approve "${charge.name}"? The guest will be notified.`}
                    onOK={() => {
                      presentLoading();
                      ChargeService.approve(charge._id).then(
                        () => {
                          dismissLoading();
                          presentToastSuccess(
                            "Service approved. A email has been sent to the guest.",
                          );
                          refreshDetails(bookingDBId);
                        },
                        (error) => {
                          dismissLoading();
                          presentToastErrorContent(error);
                        },
                      );
                    }}
                    buttonOKText={t("common.approve")}
                  />
                </Box>
              ))}
          </Box>
        </Box>
        {charge.status === "requested" &&
        charge.reference === "earlyCheckin" &&
        formValues.hasLinkedBookings ? (
          <Text size="small" color="status-error">
            Multi-room reservation — discuss pricing with the guest before
            approving.
          </Text>
        ) : charge?.product?.commerceConnection ? (
          <Text size="small">
            {charge.quantity} x {charge.price} ={" "}
            {charge.quantity * charge.price}{" "}
            {charge.product.priceCurrency || hotel.currency}
          </Text>
        ) : charge?.product ? (
          <Text size="small">
            {charge.quantity} x {charge.product.price} + {charge.product.tax} ={" "}
            {charge.quantity * (charge.product.price + charge.product.tax)}{" "}
            {hotel.currency}
          </Text>
        ) : charge.reference === "menu" ? (
          <Text size="small">
            {(charge.items || []).reduce(
              (count, item) => count + Number(item.quantity || 0),
              0,
            )}{" "}
            {(charge.items || []).reduce(
              (count, item) => count + Number(item.quantity || 0),
              0,
            ) === 1
              ? "item"
              : "items"}{" "}
            · {formatMoney(getMenuSubtotal(charge.items), hotel.currency)}
          </Text>
        ) : (
          <Text size="small">
            {charge.quantity} x {charge.price} ={" "}
            {charge.quantity * charge.price} {hotel.currency}
          </Text>
        )}
      </Box>
    );
  };

  const handleSendMessage = () => {
    setTabIndex(1);
  };

  const hasKeys = useMemo(
    () =>
      (formValues.ekeys || []).some((key) =>
        key.bookings.find((x) => x._id === formValues._id),
      ) ||
      (formValues.passcodes || []).some((key) =>
        key.bookings.find((x) => x._id === formValues._id),
      ),
    [formValues._id, formValues.ekeys, formValues.passcodes],
  );

  const areRoomOrDatesTouched = useMemo(
    () =>
      formValues.roomNumber !== booking.roomNumber ||
      !moment(formValues.checkinDate).isSame(booking.checkinDate) ||
      !moment(formValues.checkoutDate).isSame(booking.checkoutDate) ||
      formValues.checkinTime !==
        moment.tz(booking.checkinDate, hotel.timezone).format("HH:mm") ||
      formValues.checkoutTime !==
        moment.tz(booking.checkoutDate, hotel.timezone).format("HH:mm"),
    [
      formValues.roomNumber,
      formValues.checkinDate,
      formValues.checkoutDate,
      formValues.checkinTime,
      formValues.checkoutTime,
      booking.roomNumber,
      booking.checkinDate,
      booking.checkoutDate,
      hotel.timezone,
    ],
  );

  const allOfflineCodes = useMemo(
    () =>
      (formValues.passcodes || []).every(
        (key) =>
          key.offline || !key.bookings.some((x) => x._id === formValues._id),
      ),
    [formValues.passcodes, formValues._id],
  );

  const isRoomValid = useMemo(
    () =>
      doors.some(
        (door) =>
          (door.lockId || door.validNoLock) && door.name === booking.roomNumber,
      ),
    [booking.roomNumber, doors],
  );

  const passcodeDeliverySummary = useMemo(() => {
    // Hotels that disabled all communications never tried to send the
    // passcode SMS/email — don't surface a "delivery failed" banner.
    if (formValues.hotel?.disableAllCommunications) {
      return { showWarning: false };
    }

    const currentBookingPasscodes = (formValues.passcodes || []).filter((p) =>
      (p.bookings || []).some((b) => b._id === formValues._id),
    );

    if (!currentBookingPasscodes.length) {
      return {
        showWarning: false,
      };
    }

    // Codes exist but the booking has no phone AND no email — there is
    // no channel through which the server could ever deliver them.
    // Staff need to be told so they can share the codes another way.
    const hasPhone = Boolean(formValues.phone);
    const hasEmail = Boolean(formValues.email);
    if (!hasPhone && !hasEmail) {
      return {
        showWarning: true,
        noContactInfo: true,
        totalCount: currentBookingPasscodes.length,
      };
    }

    // A passcode counts as "failed delivery" only when BOTH the SMS and
    // the email notification failed (or were not sent). If either channel
    // was delivered, the guest got their code.
    //
    // Defensive guard: a passcode whose lastNotification has neither a
    // status NOR a Twilio messageSid / SES messageId on either channel
    // was never actually dispatched (older codes-only path, or a server
    // bug that stamped only `{sentAt,to}`). Treating those as "failed"
    // produces a permanent yellow banner that staff can never clear,
    // because the Twilio webhook can't match a passcode without
    // messageSid. Surface a banner only when at least one channel shows
    // evidence of dispatch + a non-success status.
    const successStatuses = new Set(["sent", "delivered"]);
    const failedPasscodes = currentBookingPasscodes.filter((p) => {
      const textStatus = (p.lastNotification?.text?.status || "").toLowerCase();
      const emailStatus = (
        p.lastNotification?.email?.status || ""
      ).toLowerCase();
      const textDispatched = Boolean(
        textStatus || p.lastNotification?.text?.messageSid,
      );
      const emailDispatched = Boolean(
        emailStatus || p.lastNotification?.email?.messageId,
      );
      // Never tried either channel → unknown, not failed.
      if (!textDispatched && !emailDispatched) return false;
      const textOk = successStatuses.has(textStatus);
      const emailOk = successStatuses.has(emailStatus);
      return !textOk && !emailOk;
    });

    if (!failedPasscodes.length) {
      return {
        showWarning: false,
      };
    }

    const retryEligibleCount = failedPasscodes.filter(
      (p) => p.lastNotification?.text?.errorCode === "30003",
    ).length;

    // A passcode is "opted-out" when the SMS failure reason is the
    // guest's own opt-out. The server stamps errorCode "OPTED_OUT" on
    // new records; older rows only carry the literal "Opted out of SMS"
    // message, so we fall back to a substring match for backward compat.
    const optedOutSms = (notif) => {
      if (notif?.errorCode === "OPTED_OUT") return true;
      const msg = notif?.errorMessage;
      return typeof msg === "string" && msg.toLowerCase().includes("opted out");
    };
    const optedOutCount = failedPasscodes.filter((p) =>
      optedOutSms(p.lastNotification?.text),
    ).length;
    const allOptedOut =
      optedOutCount > 0 && optedOutCount === failedPasscodes.length;

    return {
      showWarning: true,
      failedCount: failedPasscodes.length,
      totalCount: currentBookingPasscodes.length,
      retryEligibleCount,
      optedOutCount,
      allOptedOut,
    };
  }, [
    formValues.passcodes,
    formValues._id,
    formValues.phone,
    formValues.email,
    formValues.hotel?.disableAllCommunications,
  ]);

  const handleSendInvitation = () => {
    presentLoading();
    GuestService.sendInvitation(formValues._id).then(
      (response) => {
        dismissLoading();
        presentToastSuccess("Invitation sent");
        refreshDetails(bookingDBId);
      },
      (error) => {
        dismissLoading();
        presentToastErrorContent(error);
      },
    );
  };

  const filterHours =
    (type) =>
    ({ value }) => {
      // if hotel is using oracode
      // if type === 'checkin' then only allow hours at default checkin time or default early-checkin time
      // if type === 'checkout' then only allow hours at default checkout time or default late-checkout time
      return (
        !isOracode ||
        (type === "checkin"
          ? [hotel.checkinTime, hotel.earlyCheckinTime].includes(value)
          : [hotel.checkoutTime, hotel.lateCheckoutTime].includes(value))
      );
    };

  const shouldShowBookingName =
    (booking.bookingFirstname || booking.bookingLastname) &&
    (formValues.firstname !== booking.bookingFirstname ||
      formValues.lastname !== booking.bookingLastname);

  return formValues._id ? (
    <Box
      align="center"
      justify="start"
      pad="medium"
      gap="medium"
      flex="grow"
      margin={{ bottom: "300px" }}
    >
      <Header
        align="center"
        direction="row"
        fill="horizontal"
        className="details-header"
      >
        <Button
          label={t("guestDetails.backToList")}
          primary
          onClick={handleBack}
          className="back-button"
        />
        <Heading level="2" className="heading">
          {t("guestDetails.reservationNumber", {
            bookingId: formValues.bookingId,
          })}
        </Heading>
      </Header>
      <Tabs
        key="info"
        activeIndex={tabIndex}
        onActive={onActive}
        className="details-tabs"
      >
        <Tab title={t("guestDetails.information")}>
          {(() => {
            const showSms = !!formValues.smsUnreachable;
            const showPasscode =
              caps.accessDoors && passcodeDeliverySummary.showWarning;
            const showFallback =
              !showSms && !showPasscode && !!formValues.commsFailed;
            if (!showSms && !showPasscode && !showFallback) return null;

            const noOtherChannel =
              showSms && (!formValues.email || formValues.emailUnreachable);
            // Severity:
            //  - critical (red solid):  SMS dead AND no email fallback
            //  - warning (orange solid): we tried to notify and failed
            //                            (passcode SMS failed, or server flag)
            //  - info (white + orange border): SMS dead but email still works
            const isCritical = noOtherChannel;
            const isWarning = !isCritical && (showPasscode || showFallback);
            const background = isCritical
              ? "status-critical"
              : isWarning
                ? "status-warning"
                : "white";
            const border =
              !isCritical && !isWarning
                ? { color: "status-warning", size: "small" }
                : undefined;
            const iconColor =
              isCritical || isWarning ? "white" : "status-warning";
            const textColor = isCritical || isWarning ? "white" : "dark-1";

            return (
              <Box
                gap="xsmall"
                pad="medium"
                round="small"
                background={background}
                border={border}
                margin={{ bottom: "medium" }}
              >
                <Box direction="row" align="start" gap="small">
                  <StatusWarning color={iconColor} />
                  <Box gap="xsmall">
                    {showSms && (
                      <Text color={textColor} weight="bold">
                        {!noOtherChannel
                          ? t("comms.bannerSmsOnlyEmailFallback", {
                              phone:
                                formValues.phone || t("comms.bannerThisNumber"),
                            })
                          : t("comms.bannerSmsAndNoEmail", {
                              phone:
                                formValues.phone || t("comms.bannerThisNumber"),
                            })}
                      </Text>
                    )}
                    {showPasscode && (
                      <>
                        {passcodeDeliverySummary.noContactInfo ? (
                          <>
                            <Text color={textColor} weight="bold">
                              {t("comms.passcodesNoContactInfo")}
                            </Text>
                            <Text color={textColor} size="small">
                              {t("comms.passcodeNoContactInfoHint")}
                            </Text>
                          </>
                        ) : passcodeDeliverySummary.allOptedOut ? (
                          <>
                            <Text color={textColor} weight="bold">
                              {t("comms.passcodesOptedOut")}
                            </Text>
                            <Text color={textColor} size="small">
                              {t("comms.passcodeOptedOutHint")}
                            </Text>
                          </>
                        ) : passcodeDeliverySummary.optedOutCount > 0 ? (
                          <>
                            <Text color={textColor} weight="bold">
                              {t("comms.passcodesMixedFailures", {
                                optedOut: passcodeDeliverySummary.optedOutCount,
                                failed:
                                  passcodeDeliverySummary.failedCount -
                                  passcodeDeliverySummary.optedOutCount,
                              })}
                            </Text>
                            <Text color={textColor} size="small">
                              {t("comms.passcodeOptedOutHint")}
                            </Text>
                            <Text color={textColor} size="small">
                              {passcodeDeliverySummary.retryEligibleCount > 0
                                ? t("comms.passcodeRetryScheduled")
                                : t("comms.passcodeNoRetry")}
                            </Text>
                          </>
                        ) : (
                          <>
                            <Text color={textColor} weight="bold">
                              {t("comms.passcodesFailed")}
                            </Text>
                            <Text color={textColor} size="small">
                              {passcodeDeliverySummary.retryEligibleCount > 0
                                ? t("comms.passcodeRetryScheduled")
                                : t("comms.passcodeNoRetry")}
                            </Text>
                          </>
                        )}
                      </>
                    )}
                    {showFallback && (
                      <Text color={textColor} weight="bold">
                        {t("comms.commsFailedBadge")}
                      </Text>
                    )}
                  </Box>
                </Box>
              </Box>
            );
          })()}
          <Grid
            className="home-page"
            areas={gridAreas[contextSize]}
            columns={[
              ["auto", "600px"],
              ["auto", "400px"],
            ]}
            rows={rows[contextSize]}
            gap="medium"
          >
            <Box
              gridArea="info"
              pad="medium"
              gap="small"
              background={disabled() ? "disabled-background" : "light-1"}
              round
            >
              <Box direction="row" gap="small">
                <FormField label={t("guestModal.firstName")} alignSelf="end">
                  <TextInput
                    name="firstname"
                    value={formValues.firstname}
                    onChange={handleInputChange}
                  />
                </FormField>
                <FormField label={t("guestModal.lastName")} alignSelf="end">
                  <TextInput
                    name="lastname"
                    value={formValues.lastname}
                    onChange={handleInputChange}
                  />
                </FormField>
              </Box>
              {shouldShowBookingName && (
                <Text color="grey" margin={{ top: "-20px" }}>
                  {t("guestDetails.reservationName", {
                    firstName: booking.bookingFirstname,
                    lastName: booking.bookingLastname,
                  })}
                </Text>
              )}
              <BookingCalendarInput
                checkinDate={moment.tz(formValues.checkinDate, hotel.timezone)}
                checkoutDate={moment.tz(
                  formValues.checkoutDate,
                  hotel.timezone,
                )}
                onChange={handleBookingDatesChange}
                disabled={disabled()}
              />

              <Box direction="row" gap="small">
                <FormField label={t("guestDetails.checkinTime")}>
                  <HourInput
                    name="checkinTime"
                    value={formValues.checkinTime}
                    onChange={handleHourInputChange}
                    disabled={disabled()}
                    timezone={hotel.timezone}
                    filter={filterHours("checkin")}
                  />
                </FormField>
                <FormField label={t("guestDetails.checkoutTime")}>
                  <HourInput
                    name="checkoutTime"
                    value={formValues.checkoutTime}
                    onChange={handleHourInputChange}
                    disabled={disabled()}
                    timezone={hotel.timezone}
                    filter={filterHours("checkout")}
                  />
                </FormField>
              </Box>
              <FormField label={t("guestDetails.roomNumber")}>
                <RoomSelect
                  value={formValues.roomNumber}
                  onChange={handleRoomInputChange}
                  disabled={disabled()}
                />
              </FormField>
              <FormField label={t("guestModal.email")}>
                <TextInput
                  name="email"
                  value={formValues.email}
                  onChange={handleInputChange}
                  placeholder="example@my.com"
                />
              </FormField>
              <Button
                label={t("common.save")}
                onClick={() => handleUpdate("info")}
                primary
                alignSelf="center"
                disabled={disabled()}
              />
            </Box>
            <Box
              gridArea="status"
              pad="medium"
              gap="medium"
              background={disabled() ? "disabled-background" : "light-1"}
              round
              align="center"
            >
              {formValues.onlineCheckin &&
              formValues.onlineCheckin.photoSelfie ? (
                <SecuredImage
                  height="250px"
                  filename={formValues.onlineCheckin.photoSelfie}
                />
              ) : (
                <Image
                  height="150px"
                  width="150px"
                  fit="cover"
                  src={placeholderGuest}
                  className="details-guest-photo"
                />
              )}
              <Box direction="row" align="center" gap="small" justify="center">
                <BookingStatus
                  status={formValues.status}
                  commsFailed={formValues.commsFailed}
                />
                {formValues.status?.slug === "keysent" &&
                  caps.accessDoors &&
                  hotel.lockType === "ttlock" && (
                    <CheckKeyUsedButton
                      bookingId={formValues._id}
                      disabled={moment().isBefore(formValues.checkinDate)}
                      onDetected={refreshDetails}
                    />
                  )}
              </Box>
              <Box direction="row" align="center" gap="xsmall" justify="center">
                <Text alignSelf="center" color="dark-1" weight="bold">
                  Phone Number: {formatPhoneNumberIntl(formValues.phone)}
                </Text>
                {formValues.phone && formValues.smsUnreachable && (
                  <SmsUnreachableIcon
                    pkey={`gd-smsunreachable-${formValues._id}`}
                    phone={formValues.phone}
                    color={
                      formValues.email && !formValues.emailUnreachable
                        ? "status-warning"
                        : "status-critical"
                    }
                  />
                )}
              </Box>
              <Button
                label={t("guestDetails.sendMessage")}
                onClick={handleSendMessage}
                primary
                alignSelf="center"
              />
              {!disabled() && (
                <Button
                  label={t("guestDetails.checkOut")}
                  onClick={handleCheckout}
                  primary
                  color="status-error"
                  alignSelf="center"
                />
              )}
            </Box>
            <Box
              gridArea="upsell"
              pad="medium"
              gap="small"
              background={disabled() ? "disabled-background" : "light-1"}
              round
            >
              {hotel.onlineCheckinEnabled && formValues.onlineCheckin && (
                <>
                  <Heading level={4} margin={{ bottom: "small", top: "none" }}>
                    Online Check-in
                  </Heading>
                  {formValues.hasLinkedBookings &&
                    formValues.status.slug === "toverify" && (
                      <Text
                        size="small"
                        color="status-warning"
                        margin={{ bottom: "small" }}
                        weight="bold"
                      >
                        Approving this check-in will update all linked
                        reservations.
                      </Text>
                    )}
                  <VerifyCheckInModal
                    onSubmit={handleSubmitVerification}
                    status={formValues.status.slug}
                    checkinData={formValues.onlineCheckin}
                    bookingId={formValues._id}
                    hotel={hotel}
                  />
                </>
              )}
              {formValues.parent && (
                <Box margin={{ top: "small" }}>
                  <Anchor href={`/guests/${formValues.parent}`}>
                    ↗ View parent reservation
                  </Anchor>
                </Box>
              )}
              {!disabled() && (
                <Box direction="row" gap="medium" align="center">
                  {hotel.onlineCheckinEnabled &&
                    formValues.invitationSent &&
                    formValues.status.slug !== "onlinecheckin" &&
                    !hasKeys && (
                      <ButtonWithConfirm
                        label={t("guestDetails.restartCheckin")}
                        secondary
                        alignSelf="center"
                        color="status-warning"
                        onOK={() => handleUpdate("upsell", true)}
                        buttonOKText={t("guestDetails.restartCheckin")}
                        alertMessage="This will restart the check-in process. The guest will be warned by text/email/notification."
                      />
                    )}
                  {hotel.onlineCheckinEnabled &&
                    formValues.status.slug === "onlinecheckin" &&
                    !hasKeys &&
                    !formValues.parent && (
                      <ButtonWithConfirm
                        label={
                          formValues.invitationSent
                            ? "Resend pre-check-in invite"
                            : "Send pre-check-in invite"
                        }
                        primary
                        alignSelf="center"
                        color="blue-1"
                        onOK={handleSendInvitation}
                        buttonOKText={t("conversation.send")}
                        alertMessage={`This will send the online pre-check-in invitation${
                          formValues.invitationSent ? " again" : ""
                        }.`}
                      />
                    )}
                </Box>
              )}
              {(caps.upsells || formValues.charges.length > 0) && (
                <Heading level={4} margin={{ top: "medium", bottom: "small" }}>
                  Upsell
                </Heading>
              )}
              {caps.upsells && formValues.charges.length === 0 && (
                <Text color="dark-1">
                  {t("guestDetails.noAdditionalServices")}
                </Text>
              )}
              {formValues.charges.map((charge) => renderCharge(charge))}

              {showEarlyCheckinOffer && (
                <CheckBox
                  name="offerEarlyCheckin"
                  checked={
                    formValues.offerEarlyCheckin &&
                    currentRoomType?.earlyCheckinPrice > 0
                  }
                  onChange={handleCheckboxChange}
                  disabled={!(currentRoomType?.earlyCheckinPrice > 0)}
                  label={
                    <Box direction="row" gap="xsmall" align="center">
                      <Text>{t("settingsRoomTypes.earlyCheckin")}</Text>
                      {currentRoomType?.earlyCheckinPrice > 0 && (
                        <Text size="small" color="dark-6">
                          ({hotel.currency} {currentRoomType.earlyCheckinPrice})
                        </Text>
                      )}
                    </Box>
                  }
                />
              )}
              {showUpgradeOffer && (
                <CheckBox
                  name="offerUpgrade"
                  checked={
                    formValues.offerUpgrade && currentRoomType?.upgradePrice > 0
                  }
                  onChange={handleCheckboxChange}
                  disabled={!(currentRoomType?.upgradePrice > 0)}
                  label={
                    <Box direction="row" gap="xsmall" align="center">
                      <Text>{t("settingsUpsell.offerRoomUpgrade")}</Text>
                      {currentRoomType?.upgradePrice > 0 && (
                        <Text size="small" color="dark-6">
                          ({hotel.currency} {currentRoomType.upgradePrice})
                        </Text>
                      )}
                    </Box>
                  }
                />
              )}
              {showLateCheckoutOffer && (
                <CheckBox
                  name="offerLateCheckout"
                  checked={
                    formValues.offerLateCheckout &&
                    currentRoomType?.lateCheckoutPrice > 0
                  }
                  onChange={handleCheckboxChange}
                  disabled={!(currentRoomType?.lateCheckoutPrice > 0)}
                  label={
                    <Box direction="row" gap="xsmall" align="center">
                      <Text>{t("settingsUpsell.offerLateCheckout")}</Text>
                      {currentRoomType?.lateCheckoutPrice > 0 && (
                        <Text size="small" color="dark-6">
                          ({hotel.currency} {currentRoomType.lateCheckoutPrice})
                        </Text>
                      )}
                    </Box>
                  }
                />
              )}
              {!disabled() &&
                (showEarlyCheckinOffer ||
                  showUpgradeOffer ||
                  showLateCheckoutOffer) && (
                  <Button
                    label={t("common.update")}
                    onClick={() => handleUpdate("upsell")}
                    primary
                    color="brand"
                    alignSelf="start"
                  />
                )}
            </Box>
            {caps.accessDoors && (
              <Box
                gridArea="keys"
                pad="medium"
                gap="medium"
                background={disabled() ? "disabled-background" : "#f5efff"}
                round
              >
                <Heading level={4} margin="none">
                  {isOracode
                    ? t("guestDetails.sendPasscodes")
                    : t("guestDetails.sendMobileKeysAndPasscodes")}
                </Heading>
                <Box direction="row" gap="small" align="center">
                  <Text>{t("guestDetails.room")}:</Text>
                  {booking.roomNumber ? (
                    <Box direction="row" gap="xsmall" align="center">
                      <Box
                        border={{
                          size: "small",
                          color: !isRoomValid ? "status-error" : "brand",
                        }}
                        round
                        pad={{ horizontal: "5px" }}
                      >
                        <Text
                          weight="bold"
                          color={!isRoomValid ? "status-error" : null}
                          style={
                            !isRoomValid
                              ? { textDecoration: "line-through" }
                              : {}
                          }
                        >
                          {booking.roomNumber}
                        </Text>
                      </Box>
                      {canRemoteUnlock &&
                        isRoomValid &&
                        accessDoors.find(
                          (d) => d.name === booking.roomNumber && d.lockId,
                        ) && (
                          <RemoteUnlockButton
                            door={accessDoors.find(
                              (d) => d.name === booking.roomNumber,
                            )}
                            disabled={disabled()}
                          />
                        )}
                    </Box>
                  ) : (
                    <Text>{t("common.unassigned")}</Text>
                  )}
                </Box>
                {accessDoors.filter((x) => x.type === "area")?.length > 0 && (
                  <Box direction="row" gap="small">
                    <Text>{t("guestDetails.commonAreas")}:</Text>
                    <Box gap="xsmall">
                      {(accessDoors || [])
                        .filter((x) => x.type === "area")
                        .map((door) => (
                          <Box
                            key={door._id}
                            direction="row"
                            gap="xsmall"
                            align="center"
                          >
                            <Box
                              border={{ size: "small", color: "brand" }}
                              round
                              pad={{ horizontal: "5px" }}
                              width="fit-content"
                            >
                              <Text weight="bold" truncate>
                                {door.name}
                              </Text>
                            </Box>
                            {canRemoteUnlock && door.lockId && (
                              <RemoteUnlockButton
                                door={door}
                                disabled={disabled()}
                              />
                            )}
                          </Box>
                        ))}
                    </Box>
                  </Box>
                )}
                {booking && (
                  <Box direction="row" justify="between" align="center">
                    <Box
                      style={{ width: "fit-content", margin: "auto" }}
                      pad={{ horizontal: "small" }}
                      align="center"
                      round="small"
                      border={{ color: "status-ok", size: "small" }}
                    >
                      <Text size="xsmall" color="status-ok">
                        {moment
                          .tz(booking.checkinDate, hotel.timezone)
                          .format("ll")}
                      </Text>
                      <Text size="xsmall" color="status-ok">
                        {moment
                          .tz(booking.checkinDate, hotel.timezone)
                          .format("LT")}
                      </Text>
                    </Box>
                    <FormNextLink />
                    <Box
                      style={{ width: "fit-content", margin: "auto" }}
                      pad={{ horizontal: "small" }}
                      align="center"
                      round="small"
                      border={{ color: "status-error", size: "small" }}
                    >
                      <Text size="xsmall" color="status-error">
                        {moment
                          .tz(booking.checkoutDate, hotel.timezone)
                          .format("ll")}
                      </Text>
                      <Text size="xsmall" color="status-error">
                        {moment
                          .tz(booking.checkoutDate, hotel.timezone)
                          .format("LT")}
                      </Text>
                    </Box>
                  </Box>
                )}
                {!isOracode &&
                  hotel?.usePasscodes &&
                  hotel?.hasWifiGateway &&
                  !hotel.useRandomCode && (
                    <Box alignSelf="center">
                      <CheckBox
                        name="forceRandomPasscode"
                        checked={formValues.forceRandomPasscode}
                        onChange={handleUpdateForceRandomPasscode}
                        label={t("guestDetails.forceRandomPasscode")}
                        disabled={
                          areRoomOrDatesTouched ||
                          !booking.roomNumber ||
                          !isRoomValid
                        }
                      />
                    </Box>
                  )}
                {!isOracode && (
                  <Box alignSelf="center">
                    <CheckBox
                      name="validNow"
                      checked={formValues.validNow}
                      onChange={handleValidNowChange}
                      label={t("guestDetails.validNow")}
                      disabled={
                        areRoomOrDatesTouched ||
                        !booking.roomNumber ||
                        !isRoomValid
                      }
                    />
                  </Box>
                )}
                <Box direction="row" gap="small" alignSelf="center">
                  <Button
                    label={
                      !hasKeys || isOracode
                        ? t("guestModal.sendKeys")
                        : t("guestDetails.updateKeys")
                    }
                    onClick={() => handleSendKeys()}
                    primary
                    color="brand"
                    disabled={
                      areRoomOrDatesTouched ||
                      !booking.roomNumber ||
                      !isRoomValid
                    }
                  />
                  {areRoomOrDatesTouched && (
                    <Tip
                      content={
                        <>
                          <Text>{t("guestDetails.roomOrDatesChanged")}</Text>
                          <Text>{t("guestDetails.saveBeforeSendingKeys")}</Text>
                        </>
                      }
                    >
                      <StatusWarning color="status-warning" />
                    </Tip>
                  )}
                  {!isRoomValid && !areRoomOrDatesTouched && (
                    <Tip content={t("guestDetails.configureSmartLock")}>
                      <StatusWarning color="status-warning" />
                    </Tip>
                  )}
                </Box>
                {(!isOracode || hasRevocableAccess(formValues, hotel)) && (
                  <ButtonWithConfirm
                    label={t("guestDetails.deleteAllKeys")}
                    primary
                    alignSelf="center"
                    color="status-error"
                    disabled={
                      !hasKeys || areRoomOrDatesTouched || allOfflineCodes
                    }
                    onOK={deleteAllKeys}
                    buttonOKText={t("common.delete")}
                    alertMessage={t("guestDetails.deleteAllKeysMessage")}
                  />
                )}
                <KeysSentReportModal
                  report={keysSentReport}
                  isSchlage={hotel?.lockType === "schlage"}
                  onClose={closeKeysSentReport}
                  onRefresh={refreshKeysReport}
                  isOracode={isOracode}
                  canUseOfflineFallback={supportsOfflineFallback(hotel)}
                  onRetryWithOfflineCodes={() =>
                    handleSendKeys("retry_offline")
                  }
                />
              </Box>
            )}
            {
              <Box
                gridArea="magicLink"
                pad="medium"
                gap="small"
                background={disabled() ? "disabled-background" : "light-1"}
                round
              >
                <Heading level={4} margin="none">
                  {t("guestDetails.mobileAppMagicLink")}
                </Heading>
                {formValues.magicLink && (
                  <Box direction="row" gap="medium" align="center">
                    {formValues.magicLink}
                    <CopyToClipboard text={formValues.magicLink}>
                      <Button primary label={t("settingsPms.copy")} />
                    </CopyToClipboard>
                    {formValues.linkUsed && (
                      <Box
                        pad={{ horizontal: "small", vertical: "2px" }}
                        round="small"
                        border={{ color: "status-ok", size: "small" }}
                      >
                        <Text size="12px" color="status-ok">
                          {t("guestDetails.linkUsed")}
                        </Text>
                      </Box>
                    )}
                  </Box>
                )}
              </Box>
            }
            {caps.accessDoors && hotel?.usePasscodes && (
              <Box
                gridArea="passcode"
                pad="medium"
                gap="small"
                background={disabled() ? "disabled-background" : "light-1"}
                round
              >
                <AccessList
                  title={t("passcodes.title")}
                  keys={formValues.passcodes}
                  bookingId={formValues._id}
                  hotel={hotel}
                  onRefresh={
                    hotel?.lockType === "schlage"
                      ? refreshAccess
                      : refreshDetails
                  }
                  refreshError={accessRefreshError}
                  otherKeys={formValues.ekeys}
                />
              </Box>
            )}
            {caps.accessDoors && !isOracode && (
              <Box
                gridArea="ekeys"
                pad="medium"
                gap="small"
                background={disabled() ? "disabled-background" : "light-1"}
                round
              >
                <AccessList
                  title={t("guestDetails.mobileKeys")}
                  keys={formValues.ekeys}
                  bookingId={formValues._id}
                  hotel={hotel}
                  onRefresh={refreshDetails}
                  collapsing={hotel.sendMobileKeyQuietly}
                  otherKeys={formValues.passcodes}
                />
              </Box>
            )}
          </Grid>
        </Tab>

        <Tab
          key="conversation"
          title={t("guestDetails.conversation")}
          className={
            (hotel.notifications || []).filter(
              (x) => x.booking === bookingDBId && x.type === "message",
            ).length > 0 && "has-new-messages"
          }
        >
          <Conversation
            bookingId={bookingDBId}
            guestName={`${formValues.firstname} ${formValues.lastname}`}
            hotel={hotel}
            phone={formValues.phone}
            email={formValues.email}
            smsUnreachable={!!formValues.smsUnreachable}
            emailUnreachable={!!formValues.emailUnreachable}
          />
        </Tab>
        <Tab title={t("guestDetails.history")} key="events">
          <EventList bookingId={bookingDBId} timezone={hotel?.timezone} />
        </Tab>
      </Tabs>
    </Box>
  ) : (
    <div></div>
  );
}
